// src/pages/projects/projectMaterialService.js

import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDocs,
  query, where, serverTimestamp, getDoc, runTransaction,
} from "firebase/firestore";
import { db } from "../../firebase";

export const STAGE_STATUS = {
  locked:           { label: "Locked",          color: "bg-gray-100 text-gray-500" },
  unlocked:         { label: "Unlocked",         color: "bg-blue-100 text-blue-700" },
  partially_issued: { label: "Partially Issued", color: "bg-amber-100 text-amber-700" },
  fully_issued:     { label: "Fully Issued",     color: "bg-green-100 text-green-700" },
};

export const ALERT_LEVEL = {
  ok:        { label: "OK",        color: "text-green-600", bg: "bg-green-50"  },
  warning:   { label: "Warning",   color: "text-amber-600", bg: "bg-amber-50"  },
  exhausted: { label: "Exhausted", color: "text-red-600",   bg: "bg-red-50"    },
  // NEW — covers a real cosmetic gap: qty_budgeted reduced to 0 (e.g. via
  // Task C's Edit Items) while qty_issued > 0. getAlertLevel() below used
  // to fall through to "ok" here purely because of its qty_budgeted===0
  // early-return guard, showing a misleadingly green badge on a genuine
  // deficit. Never written retroactively onto old stage docs — only
  // applies the next time this item's alert_level is recalculated
  // (issue, edit, or variation).
  variation: { label: "⚠️ Variation", color: "text-red-700",  bg: "bg-red-100"   },
};

export const MRF_STATUS = {
  pending:  { label: "Pending",  color: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", color: "bg-blue-100 text-blue-700"   },
  issued:   { label: "Issued",   color: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700"     },
  returned: { label: "Returned", color: "bg-purple-100 text-purple-700" },
};

export const getAlertLevel = (qty_issued, qty_budgeted) => {
  // FIXED — previously returned "ok" (green) whenever qty_budgeted was 0,
  // with no regard for qty_issued. That's correct for a genuinely empty
  // budget row (0 issued, 0 budgeted — nothing to alert on), but wrong for
  // a real deficit: budget reduced to 0 (or below) while material had
  // already been issued against it. That specific case now surfaces as
  // "variation" instead of silently reading as healthy.
  if (!qty_budgeted || qty_budgeted === 0) {
    return Number(qty_issued || 0) > 0 ? "variation" : "ok";
  }
  const pct = (qty_issued / qty_budgeted) * 100;
  if (pct < 75)  return "ok";
  if (pct < 100) return "warning";
  return "exhausted";
};

const getNextMrfNo = async (company_id) => {
  const snap = await getDocs(query(
    collection(db, "project_mrfs"),
    where("company_id", "==", company_id)
  ));
  return `PMRF-${String(snap.size + 1).padStart(4, "0")}`;
};

const getNextStageNo = async (company_id, project_id) => {
  const counterRef = doc(db, "system_counters", `stageno_${project_id}`);

  // One-time seed value — ONLY used the very first time a counter doc is
  // created for this project. Reads the current max stage_no already on
  // this project's stages, so numbering continues correctly for projects
  // that already have stages (rather than restarting at 1 and colliding
  // with existing numbers). Existing duplicate numbers from BEFORE this
  // fix are not retroactively corrected — this only prevents NEW duplicates.
  let seedValue = 0;
  const existingSnap = await getDocs(query(
    collection(db, "project_material_stages"),
    where("company_id", "==", company_id),
    where("project_id", "==", project_id)
  ));
  existingSnap.docs.forEach(d => {
    const no = Number(d.data().stage_no || 0);
    if (no > seedValue) seedValue = no;
  });

  // Atomic increment — guarantees no two concurrent calls ever get the
  // same number, same discipline already used for PO/ADJ/VRT numbering.
  return runTransaction(db, async (tx) => {
    const counterSnap = await tx.get(counterRef);
    const current = counterSnap.exists()
      ? Number(counterSnap.data().last_number || 0)
      : seedValue;
    const next = current + 1;
    tx.set(counterRef, {
      counter_type: "material_stage_no",
      project_id,
      company_id,
      last_number:  next,
      updated_at:   serverTimestamp(),
    }, { merge: true });
    return next;
  });
};

export const createMaterialStage = async (data, user) => {
  const company_id = user.company_id;
  const stage_no   = await getNextStageNo(company_id, data.project_id);

  const stageData = {
    company_id,
    project_id:        data.project_id,
    project_name:      data.project_name,
    stage_no,
    stage_name:        data.stage_name,
    trigger_items:     data.trigger_items    || [], // legacy — item_no based, kept for audit/fallback only
    trigger_item_ids:  data.trigger_item_ids || [], // canonical — checklist item Firestore IDs
    status:            "locked",
    material_budget: (data.material_budget || []).map(item => {
      const side       = item.side       || "high_side";
      const buffer_pct = side === "low_side" ? (item.buffer_pct ?? 15) : 0;
      const qty_raw    = Number(item.qty_budgeted || 0);
      const qty_final  = side === "low_side"
        ? Math.ceil(qty_raw * (1 + buffer_pct / 100))
        : qty_raw;
      return {
        item_id:       item.item_id   || "",
        item_code:     item.item_code || "",
        item_name:     item.item_name || "",
        unit:          item.unit      || "Nos",
        side,
        buffer_pct,
        qty_entered:   qty_raw,
        qty_budgeted:  qty_final,
        qty_issued:    0,
        qty_remaining: qty_final,
        cost_rate:     Number(item.cost_rate || 0),
        budget_amount: qty_final * Number(item.cost_rate || 0),
        issued_amount: 0,
        alert_level:   "ok",
        budget_pct:    0,
        override_log:  [],
      };
    }),
    created_by:      user.uid,
    created_by_name: user.name || user.email || "",
    created_at:      serverTimestamp(),
    updated_at:      serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "project_material_stages"), stageData);
  return ref.id;
};

export const fetchMaterialStages = async (company_id, project_id) => {
  const snap = await getDocs(query(
    collection(db, "project_material_stages"),
    where("company_id", "==", company_id),
    where("project_id", "==", project_id)
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => a.stage_no - b.stage_no);
};

// completedItem = { id, item_no } — id is the checklist item's Firestore doc ID
// (stable even if the checklist is reordered/edited). item_no is kept ONLY as a
// fallback for stage docs created before item_id-based triggers existed —
// never used for new stages. See D-1XX in DECISION_LOG for rationale.
export const checkAndUnlockStages = async (company_id, project_id, completedItem) => {
  const stages   = await fetchMaterialStages(company_id, project_id);
  const unlocked = [];
  for (const stage of stages) {
    if (stage.status !== "locked") continue;

    const matchesById = (stage.trigger_item_ids || []).includes(completedItem.id);
    // Legacy fallback ONLY applies if this stage has no ID-based triggers at all —
    // prevents a stage from ever double-matching on both an old and new item.
    const matchesByLegacyNo = !(stage.trigger_item_ids?.length) &&
      (stage.trigger_items || []).includes(completedItem.item_no);

    if (matchesById || matchesByLegacyNo) {
      await updateDoc(doc(db, "project_material_stages", stage.id), {
        status:     "unlocked",
        updated_at: serverTimestamp(),
      });
      unlocked.push(stage.stage_name);

      // Notify Store + Managers that material can now be issued for this
      // stage. Non-blocking — a notification failure must never prevent
      // the stage from actually unlocking.
      try {
        const { notifyMaterialStageUnlocked } = await import("../../services/notificationService");
        await notifyMaterialStageUnlocked({
          stage_id:          stage.id,
          project_id,
          project_name:      stage.project_name || "",
          stage_name:        stage.stage_name,
          trigger_item_name: completedItem.item_name || "",
          company_id,
        });
      } catch (err) {
        console.warn("notifyMaterialStageUnlocked failed (non-blocking):", err);
      }
    }
  }
  return unlocked;
};

export const createProjectMRF = async (data, user) => {
  const company_id = user.company_id;
  const mrf_no     = await getNextMrfNo(company_id);

  const stageSnap = data.stage_id
    ? await getDoc(doc(db, "project_material_stages", data.stage_id))
    : null;
  const stage_override = stageSnap?.data()?.status === "locked";

  const mrf = {
    company_id,
    project_id:        data.project_id,
    project_name:      data.project_name,
    stage_id:          data.stage_id      || "",
    stage_no:          data.stage_no      || 0,
    stage_name:        data.stage_name    || "",
    mrf_no,
    items:             data.items,
    status:            "pending",
    stage_override,
    override_reason:   stage_override ? (data.override_reason || "") : "",
    requested_by:      user.uid,
    requested_by_name: user.name || user.email || "",
    requested_at:      serverTimestamp(),
    created_at:        serverTimestamp(),
    updated_at:        serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "project_mrfs"), mrf);
  return { id: ref.id, mrf_no };
};

export const fetchProjectMRFs = async (company_id, project_id) => {
  // Fetch from both collections and merge
  const [oldSnap, newSnap] = await Promise.all([
    getDocs(query(
      collection(db, "project_mrfs"),
      where("company_id", "==", company_id),
      where("project_id", "==", project_id)
    )),
    getDocs(query(
      collection(db, "material_requests"),
      where("company_id", "==", company_id),
      where("project_id", "==", project_id),
      where("source",     "==", "project_stage")
    )),
  ]);

  const oldMRFs = oldSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const newMRFs = newSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  const allMRFs = [...oldMRFs, ...newMRFs];

  return allMRFs.sort((a, b) => {
    const aTime = a.created_at?.seconds || 0;
    const bTime = b.created_at?.seconds || 0;
    return bTime - aTime;
  });
};

export const approveProjectMRF = async (mrf_id, user, collection_name = "project_mrfs") => {
  await updateDoc(doc(db, collection_name, mrf_id), {
    status:           "approved",
    approved_by:      user.uid,
    approved_by_name: user.name || user.email || "",
    approved_at:      serverTimestamp(),
    updated_at:       serverTimestamp(),
  });
};

export const rejectProjectMRF = async (mrf_id, reason, user, collection_name = "project_mrfs") => {
  await updateDoc(doc(db, collection_name, mrf_id), {
    status:           "rejected",
    rejection_reason: reason,
    rejected_by:      user.uid,
    rejected_by_name: user.name || user.email || "",
    rejected_at:      serverTimestamp(),
    updated_at:       serverTimestamp(),
  });
};

export const markMRFIssued = async (mrf_id, stage_id, issued_items, user, collection_name = "project_mrfs") => {
  await updateDoc(doc(db, collection_name, mrf_id), {
    status:          "issued",
    issued_by:       user.uid,
    issued_by_name:  user.name || user.email || "",
    issued_at:       serverTimestamp(),
    updated_at:      serverTimestamp(),
  });

  if (!stage_id) return;

  const stageRef  = doc(db, "project_material_stages", stage_id);
  const stageSnap = await getDoc(stageRef);
  if (!stageSnap.exists()) return;

  const stageData     = stageSnap.data();
  const updatedBudget = (stageData.material_budget || []).map(budgetItem => {
    const issued = issued_items.find(i => i.item_id === budgetItem.item_id);
    if (!issued) return budgetItem;
    const new_qty_issued    = budgetItem.qty_issued + Number(issued.qty_issued || 0);
    const new_qty_remaining = Math.max(0, budgetItem.qty_budgeted - new_qty_issued);
    const new_issued_amount = new_qty_issued * budgetItem.cost_rate;
    const new_pct           = budgetItem.qty_budgeted > 0
      ? Math.round((new_qty_issued / budgetItem.qty_budgeted) * 100) : 0;
    return {
      ...budgetItem,
      qty_issued:     new_qty_issued,
      qty_remaining:  new_qty_remaining,
      issued_amount:  new_issued_amount,
      budget_pct:     new_pct,
      alert_level:    getAlertLevel(new_qty_issued, budgetItem.qty_budgeted),
    };
  });

  const allIssued  = updatedBudget.every(i => i.qty_remaining === 0);
  const someIssued = updatedBudget.some(i => i.qty_issued > 0);

  await updateDoc(stageRef, {
    material_budget: updatedBudget,
    status:          allIssued ? "fully_issued" : someIssued ? "partially_issued" : "unlocked",
    updated_at:      serverTimestamp(),
  });
};

// ─── DIRECT SITE PURCHASE → MATERIAL CONTROL SYNC ──────────────────────────
// Mirrors markMRFIssued()'s stage-update logic exactly (same qty_issued/
// qty_remaining/issued_amount/budget_pct/alert_level recalculation), but
// checks EVERY stage in the project rather than one stage_id — a Direct
// Site Purchase PO isn't tied to a single MRF/stage the way a Store issue
// is, so an item on that PO could belong to any stage's material_budget.
// Without this call, material that goes vendor → site directly (the
// majority case in HVAC, per business confirmation) would silently show
// as "0 issued" in Material Control even though it's genuinely on site —
// this closes that gap so Material Control stays a single source of
// truth regardless of which procurement path the material took.
export const applyDirectSiteIssueToStages = async (company_id, project_id, issuedItems) => {
  if (!project_id || !issuedItems?.length) return;

  const stages = await fetchMaterialStages(company_id, project_id);

  for (const stage of stages) {
    const budget = stage.material_budget || [];
    let touched  = false;

    const updatedBudget = budget.map(budgetItem => {
      const issued = issuedItems.find(i => i.item_id === budgetItem.item_id);
      if (!issued) return budgetItem;
      touched = true;

      const new_qty_issued    = Number(budgetItem.qty_issued || 0) + Number(issued.qty_issued || 0);
      const new_qty_remaining = Math.max(0, budgetItem.qty_budgeted - new_qty_issued);
      const new_issued_amount = new_qty_issued * budgetItem.cost_rate;
      const new_pct           = budgetItem.qty_budgeted > 0
        ? Math.round((new_qty_issued / budgetItem.qty_budgeted) * 100) : 0;

      return {
        ...budgetItem,
        qty_issued:     new_qty_issued,
        qty_remaining:  new_qty_remaining,
        issued_amount:  new_issued_amount,
        budget_pct:     new_pct,
        alert_level:    getAlertLevel(new_qty_issued, budgetItem.qty_budgeted),
      };
    });

    if (!touched) continue; // this stage has none of the confirmed items — skip, no write

    const allIssued  = updatedBudget.every(i => i.qty_remaining === 0);
    const someIssued = updatedBudget.some(i => i.qty_issued > 0);

    await updateDoc(doc(db, "project_material_stages", stage.id), {
      material_budget: updatedBudget,
      status: allIssued ? "fully_issued" : someIssued ? "partially_issued" : stage.status,
      updated_at: serverTimestamp(),
    });
  }
};

export const updateMaterialStage = async (stage_id, data) => {
  await updateDoc(doc(db, "project_material_stages", stage_id), {
    ...data,
    updated_at: serverTimestamp(),
  });
};

export const saveOverrideLog = async (stage_id, item_id, qty, reason, user) => {
  const stageRef  = doc(db, "project_material_stages", stage_id);
  const stageSnap = await getDoc(stageRef);
  if (!stageSnap.exists()) return;
  const stageData     = stageSnap.data();
  const updatedBudget = (stageData.material_budget || []).map(item => {
    if (item.item_id !== item_id) return item;
    return {
      ...item,
      override_log: [
        ...(item.override_log || []),
        { date: new Date().toISOString(), qty: Number(qty), reason, by_name: user.name || user.email || "" },
      ],
    };
  });
  await updateDoc(stageRef, { material_budget: updatedBudget, updated_at: serverTimestamp() });
};

// ─── UPDATE STAGE ITEMS (Task C — edit qty/rate, add item, remove item) ───
// Manager edits a stage's material_budget[] after initial BOQ upload —
// without this, a BOQ change means re-uploading the whole thing. Golden
// Rule: this NEVER retroactively touches what's already issued
// (qty_issued/issued_amount carry forward untouched for existing items) —
// only qty_budgeted/cost_rate change, affecting FUTURE issues only.
//
// finalItems = the caller's desired end-state array (existing rows with
// possibly-edited qty_budgeted/cost_rate/_edit_reason, plus any brand-new
// rows, with removed rows simply absent). "New" vs "existing" is decided
// HERE by matching item_id against the stage's current material_budget —
// never trusted from a client-supplied flag.
//
// Reducing an existing item's qty_budgeted below its qty_issued is a real
// "already-exceeded" case. Task B (the full variation-approval workflow)
// isn't built yet, so this records a triggers_variation flag + mandatory
// reason directly in revision_log now — a future Task B can query these
// flagged entries to build its rollup retroactively, nothing lost.
export const updateStageItems = async (stage_id, finalItems, user) => {
  const stageRef  = doc(db, "project_material_stages", stage_id);
  const stageSnap = await getDoc(stageRef);
  if (!stageSnap.exists()) {
    throw new Error("Stage not found — it may have already been deleted.");
  }

  const stageData      = stageSnap.data();
  const existingBudget = stageData.material_budget || [];
  const existingById   = new Map(existingBudget.map(item => [item.item_id, item]));
  const finalIds       = new Set(finalItems.map(item => item.item_id).filter(Boolean));

  // Removed items — present in existingBudget but absent from finalItems —
  // only allowed if nothing has been issued against them yet. Same guard
  // deleteMaterialStage() uses for a whole stage, applied per-item here.
  for (const existing of existingBudget) {
    if (!finalIds.has(existing.item_id) && Number(existing.qty_issued || 0) > 0) {
      throw new Error(
        `Cannot remove "${existing.item_name}" — material has already been issued against it. Use Return Material instead if you need to reverse it.`
      );
    }
  }

  const updatedBudget = finalItems.map(item => {
    const existing = existingById.get(item.item_id);

    // NEW ITEM — no matching existing row. No revision_log entry needed
    // (nothing prior to log against). Same low_side buffer-ceiling logic
    // as createMaterialStage(), for consistency with initial BOQ import.
    if (!existing) {
      const side       = item.side || "high_side";
      const buffer_pct = side === "low_side" ? (item.buffer_pct ?? 15) : 0;
      const qty_raw    = Number(item.qty_budgeted || 0);
      const qty_final  = side === "low_side"
        ? Math.ceil(qty_raw * (1 + buffer_pct / 100))
        : qty_raw;
      const cost_rate  = Number(item.cost_rate || 0);
      return {
        item_id:       item.item_id   || "",
        item_code:     item.item_code || "",
        item_name:     item.item_name || "",
        unit:          item.unit      || "Nos",
        side,
        buffer_pct,
        qty_entered:   qty_raw,
        qty_budgeted:  qty_final,
        qty_issued:    0,
        qty_remaining: qty_final,
        cost_rate,
        budget_amount: qty_final * cost_rate,
        issued_amount: 0,
        alert_level:   "ok",
        budget_pct:    0,
        override_log:  [],
        revision_log:  [],
      };
    }

    // EXISTING ITEM — qty_issued/issued_amount carry forward untouched.
    const new_qty_budgeted = Number(item.qty_budgeted ?? existing.qty_budgeted);
    const new_cost_rate    = Number(item.cost_rate    ?? existing.cost_rate);
    const qtyChanged       = new_qty_budgeted !== existing.qty_budgeted;
    const rateChanged      = new_cost_rate    !== existing.cost_rate;

    if (!qtyChanged && !rateChanged) return existing; // untouched — no log noise

    const reducesBelowIssued = new_qty_budgeted < Number(existing.qty_issued || 0);
    if (reducesBelowIssued && !item._edit_reason?.trim()) {
      throw new Error(
        `"${existing.item_name}": new quantity (${new_qty_budgeted}) is below what's already issued (${existing.qty_issued}). A reason is required to proceed.`
      );
    }

    const new_qty_remaining = Math.max(0, new_qty_budgeted - Number(existing.qty_issued || 0));
    const new_budget_amount = new_qty_budgeted * new_cost_rate;
    const new_issued_amount = Number(existing.qty_issued || 0) * new_cost_rate;
    const new_pct           = new_qty_budgeted > 0
      ? Math.round((Number(existing.qty_issued || 0) / new_qty_budgeted) * 100) : 0;

    return {
      ...existing,
      qty_budgeted:  new_qty_budgeted,
      qty_remaining: new_qty_remaining,
      cost_rate:     new_cost_rate,
      budget_amount: new_budget_amount,
      issued_amount: new_issued_amount,
      budget_pct:    new_pct,
      alert_level:   getAlertLevel(existing.qty_issued || 0, new_qty_budgeted),
      revision_log: [
        ...(existing.revision_log || []),
        {
          old_qty:             existing.qty_budgeted,
          new_qty:             new_qty_budgeted,
          old_rate:            existing.cost_rate,
          new_rate:            new_cost_rate,
          reason:              item._edit_reason || "",
          triggers_variation:  reducesBelowIssued,
          revised_by:          user.uid,
          revised_by_name:     user.name || user.email || "",
          revised_at:          new Date().toISOString(),
        },
      ],
    };
  });

  // Reuses the EXISTING governed updateMaterialStage() — no new write-path,
  // same discipline as Edit Triggers.
  await updateMaterialStage(stage_id, { material_budget: updatedBudget });
};

// ─── STAGE VARIATIONS (Task B — issue-time budget-exceed check) ───────────
// Threshold deciding whether an exceed needs Owner/Manager-only approval vs
// Store can self-record — soft signal only, never a hard block (there's no
// live approval-request flow to block ON, only after-the-fact recording).
const VARIATION_MAJOR_PCT  = 15;    // % of budgeted qty
const VARIATION_MAJOR_COST = 10000; // ₹ extra cost

// Called as a pre-flight check BEFORE issueAgainstMR() runs for any item —
// never after. mrfItems = [{ item_id, item_name, unit, issue_qty }], where
// issue_qty is the quantity this MRF intends to issue this round (not yet
// stock-limited — the check is against the Manager's INTENT to issue, per
// the locked design's trigger point). Checks every matching stage (an item
// can belong to more than one stage's material_budget, same as the
// existing multi-stage matching in markMRFIssued()'s caller) — never stops
// at the first match.
export const checkStageVariations = async (company_id, project_id, mrfItems) => {
  if (!project_id || !mrfItems?.length) return [];
  const stages  = await fetchMaterialStages(company_id, project_id);
  const results = [];

  mrfItems.forEach(mrfItem => {
    const itemId = String(mrfItem.item_id || "").trim();
    if (!itemId || !(Number(mrfItem.issue_qty) > 0)) return;

    stages.forEach(stage => {
      const budgetItem = (stage.material_budget || []).find(
        b => String(b.item_id || "").trim() === itemId
      );
      if (!budgetItem) return;

      const currentIssued = Number(budgetItem.qty_issued || 0);
      const budgeted       = Number(budgetItem.qty_budgeted || 0);
      const newTotal        = currentIssued + Number(mrfItem.issue_qty || 0);

      if (newTotal <= budgeted) return; // within budget — no variation

      const exceeded_qty = newTotal - budgeted;
      const extra_cost    = exceeded_qty * Number(budgetItem.cost_rate || 0);
      const is_major        = exceeded_qty > budgeted * (VARIATION_MAJOR_PCT / 100) ||
                              extra_cost > VARIATION_MAJOR_COST;

      results.push({
        stage_id:     stage.id,
        stage_name:   stage.stage_name,
        project_id,
        project_name: stage.project_name || "",
        item_id:      itemId,
        item_name:    mrfItem.item_name || budgetItem.item_name || "",
        unit:         mrfItem.unit || budgetItem.unit || "",
        cost_rate:    Number(budgetItem.cost_rate || 0),
        exceeded_qty,
        extra_cost,
        is_major,
      });
    });
  });

  return results;
};

// Called AFTER the Manager confirms the variation modal. Bumps
// qty_budgeted on every affected stage item just enough to absorb the
// exceed (so the SAME issue doesn't re-flag as exceeded once it actually
// runs), records a variation_log[] entry (who/how/why), and ALSO a
// revision_log[] entry via the same mechanism Task C uses — the "approved
// variation updates qty_budgeted via revision_log" design decision. Never
// touches qty_issued/issued_amount — those still only ever change via the
// real issue flow (issueAgainstMR()/markMRFIssued()), never here.
export const recordVariationsAndAdjustBudget = async (exceedList, approvalInfo, user) => {
  const byStage = {};
  exceedList.forEach(v => {
    if (!byStage[v.stage_id]) byStage[v.stage_id] = [];
    byStage[v.stage_id].push(v);
  });

  const summary = [];

  for (const stage_id of Object.keys(byStage)) {
    const stageRef  = doc(db, "project_material_stages", stage_id);
    const stageSnap = await getDoc(stageRef);
    if (!stageSnap.exists()) continue;
    const stageData = stageSnap.data();

    const updatedBudget = (stageData.material_budget || []).map(item => {
      const match = byStage[stage_id].find(v => v.item_id === String(item.item_id || "").trim());
      if (!match) return item;

      const new_qty_budgeted  = Number(item.qty_budgeted || 0) + match.exceeded_qty;
      const new_qty_remaining = Math.max(0, new_qty_budgeted - Number(item.qty_issued || 0));
      const new_budget_amount = new_qty_budgeted * Number(item.cost_rate || 0);

      summary.push({
        project_id:       match.project_id,
        project_name:     match.project_name,
        stage_name:       stageData.stage_name,
        item_name:        item.item_name,
        exceeded_qty:      match.exceeded_qty,
        unit:              item.unit,
        extra_cost:        match.extra_cost,
        approved_by_name:  approvalInfo.approved_by_name,
      });

      return {
        ...item,
        qty_budgeted:     new_qty_budgeted,
        qty_remaining:     new_qty_remaining,
        budget_amount:     new_budget_amount,
        alert_level:       getAlertLevel(item.qty_issued || 0, new_qty_budgeted),
        variation_status:  "approved",
        variation_log: [
          ...(item.variation_log || []),
          {
            exceeded_qty:      match.exceeded_qty,
            extra_cost:        match.extra_cost,
            approved_by_name:  approvalInfo.approved_by_name || "",
            approval_channel:  approvalInfo.approval_channel || "",
            approval_note:     approvalInfo.approval_note || "",
            recorded_by:       user.uid,
            recorded_by_name:  user.name || user.email || "",
            recorded_at:       new Date().toISOString(),
          },
        ],
        revision_log: [
          ...(item.revision_log || []),
          {
            old_qty:             item.qty_budgeted,
            new_qty:             new_qty_budgeted,
            old_rate:            item.cost_rate,
            new_rate:            item.cost_rate,
            reason:              `Variation approved: ${approvalInfo.approval_note || ""}`,
            triggers_variation:  false,
            revised_by:          user.uid,
            revised_by_name:     user.name || user.email || "",
            revised_at:          new Date().toISOString(),
          },
        ],
      };
    });

    await updateMaterialStage(stage_id, { material_budget: updatedBudget });
  }

  return summary;
};
// FILE KE BILKUL END MEIN ADD KARO:
// ─── MIGRATE LEGACY STAGE TRIGGERS: item_no → item_id ───────────────────────
// Called once per project, from migrateLegacyChecklistToHierarchy() in
// projectService.js, right after that function builds the item_no → item_id
// map for the project's checklist. Idempotent — skips any stage that already
// has trigger_item_ids set, so it's safe to call more than once.
export const migrateStageTriggersToIds = async (company_id, project_id, itemNoToIdMap) => {
  const stages = await fetchMaterialStages(company_id, project_id);
  for (const stage of stages) {
    if (stage.trigger_item_ids?.length) continue; // already migrated — skip
    const legacyNos = stage.trigger_items || [];
    if (!legacyNos.length) continue;
    const mappedIds = legacyNos.map(no => itemNoToIdMap[no]).filter(Boolean);
    if (!mappedIds.length) continue;
    await updateDoc(doc(db, "project_material_stages", stage.id), {
      trigger_item_ids: mappedIds,
      updated_at:       serverTimestamp(),
    });
  }
};

// ─── CREATE RETURN MRF ────────────────────────────────────────────────────────
export const createReturnMRF = async (data, user) => {
  const company_id = user.company_id;

  // Return MRF number — PMRF-R-XXXX
  const snap = await getDocs(query(
    collection(db, "project_mrfs"),
    where("company_id", "==", company_id),
    where("type", "==", "return")
  ));
  const mrf_no = `PMRF-R-${String(snap.size + 1).padStart(4, "0")}`;

  const mrf = {
    company_id,
    project_id:        data.project_id,
    project_name:      data.project_name,
    stage_id:          data.stage_id   || "",
    stage_no:          data.stage_no   || 0,
    stage_name:        data.stage_name || "",
    mrf_no,
    type:              "return",
    original_mrf_id:   data.original_mrf_id || "",
    original_mrf_no:   data.original_mrf_no || "",
    items:             data.items,
    return_reason:     data.return_reason || "",
    status:            "returned",
    requested_by:      user.uid,
    requested_by_name: user.name || user.email || "",
    requested_at:      serverTimestamp(),
    created_at:        serverTimestamp(),
    updated_at:        serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "project_mrfs"), mrf);

  // Update stage budget — qty_issued kam karo
  if (data.stage_id) {
    const stageRef  = doc(db, "project_material_stages", data.stage_id);
    const stageSnap = await getDoc(stageRef);
    if (stageSnap.exists()) {
      const stageData     = stageSnap.data();
      const updatedBudget = (stageData.material_budget || []).map(budgetItem => {
        const returned = data.items.find(i => i.item_id === budgetItem.item_id);
        if (!returned) return budgetItem;

        const qty_returned      = Number(returned.qty_returned || 0);
        const new_qty_issued    = Math.max(0, budgetItem.qty_issued - qty_returned);
        const new_qty_remaining = Math.min(
          budgetItem.qty_budgeted,
          budgetItem.qty_remaining + qty_returned
        );
        const new_issued_amount = new_qty_issued * budgetItem.cost_rate;
        const new_pct           = budgetItem.qty_budgeted > 0
          ? Math.round((new_qty_issued / budgetItem.qty_budgeted) * 100) : 0;

        return {
          ...budgetItem,
          qty_issued:     new_qty_issued,
          qty_remaining:  new_qty_remaining,
          issued_amount:  new_issued_amount,
          budget_pct:     new_pct,
          alert_level:    getAlertLevel(new_qty_issued, budgetItem.qty_budgeted),
        };
      });

      const allIssued  = updatedBudget.every(i => i.qty_remaining === 0);
      const someIssued = updatedBudget.some(i => i.qty_issued > 0);

      await updateDoc(stageRef, {
        material_budget: updatedBudget,
        status: allIssued ? "fully_issued" : someIssued ? "partially_issued" : "unlocked",
        updated_at: serverTimestamp(),
      });
    }
  }

  return { id: ref.id, mrf_no };
};

// ─── DELETE MATERIAL STAGE (safe — only if nothing issued yet) ────────────
// Only removes a stage that has NEVER had any material issued against it
// (qty_issued === 0 across every budget row). This is a governance guard —
// deleting a stage with real issued history would silently orphan the
// financial/inventory trail already recorded via inventory_transactions.
// If material has been issued, the caller must use Return Material instead.
export const deleteMaterialStage = async (stage_id) => {
  const stageRef  = doc(db, "project_material_stages", stage_id);
  const stageSnap = await getDoc(stageRef);
  if (!stageSnap.exists()) {
    throw new Error("Stage not found — it may have already been deleted.");
  }

  const stageData    = stageSnap.data();
  const hasAnyIssued = (stageData.material_budget || []).some(
    (item) => Number(item.qty_issued || 0) > 0
  );

  if (hasAnyIssued) {
    throw new Error(
      "Cannot delete — material has already been issued against this stage. Use Return Material instead if you need to reverse it."
    );
  }

  await deleteDoc(stageRef);
};